// src/pages/FaqPage.tsx
// Consolidated help/FAQ page. Answers the common pre- and post-purchase
// questions in one place (the binding detail still lives in the policy pages,
// which these link to). Uses native <details>/<summary> for an accessible,
// state-free accordion.

import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import PageContainer, { GapSize } from "../components/PageContainer";
import PageSection from "../components/PageSection";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

interface Faq {
  q: string;
  a: React.ReactNode;
}

const FAQS: Faq[] = [
  {
    q: "How long until my order ships?",
    a: (
      <>
        Every item is printed on demand just for you — nothing sits in a
        warehouse. Production typically takes a few business days, then you'll
        get a tracking email as soon as it ships. See our{" "}
        <Link to="/shipping">Shipping</Link> page for details.
      </>
    ),
  },
  {
    q: "Where are your products made?",
    a: (
      <>
        Our designs are created in the United States and printed here by our
        domestic print partners. The blank garments are{" "}
        <strong>imported</strong> — manufactured outside the United States and
        then printed here. More on our <Link to="/shipping">Shipping</Link>{" "}
        page.
      </>
    ),
  },
  {
    q: "What size should I order? Do the shirts run large?",
    a: (
      <>
        Our apparel is a <strong>unisex fit</strong>, which runs a bit roomier
        and boxier than a women's-cut tee. For a fitted look, size down one; for
        a relaxed look, order your usual size. Full measurements and women's-fit
        guidance are on our <Link to="/size-guide">Size Guide</Link>. Because
        each item is made to order, we can't exchange for size — so please check
        the guide before ordering.
      </>
    ),
  },
  {
    q: "Can I cancel or change my order?",
    a: (
      <>
        Yes — orders can be canceled for a full refund any time{" "}
        <strong>before production begins</strong>, and cancellations in the
        first few minutes are always free. Cancel from your confirmation email
        or the <Link to="/orders/cancel">cancellation page</Link>. Once an item
        enters production it's being made specifically for you and can no longer
        be canceled.
      </>
    ),
  },
  {
    q: "My item arrived damaged or misprinted. What do I do?",
    a: (
      <>
        We'll make it right. Email{" "}
        <a href="mailto:support@americanaandcompany.com">
          support@americanaandcompany.com
        </a>{" "}
        within 30 days of delivery with a photo and description, and approved
        claims get a replacement or full refund. See{" "}
        <Link to="/returns">Returns &amp; Cancellations</Link>.
      </>
    ),
  },
  {
    q: "How do I track my order?",
    a: (
      <>
        Use the <Link to="/orders/track">order tracking page</Link> with your
        order ID and the email you used at checkout. If you have an account,
        your orders and their status are in{" "}
        <Link to="/account">My Account</Link>.
      </>
    ),
  },
  {
    q: "What payment methods do you accept?",
    a: (
      <>
        We accept all major credit and debit cards, plus Apple Pay and Google
        Pay at checkout. Payments are processed securely by Square — we never
        see or store your card details.
      </>
    ),
  },
  {
    q: "Where do you ship?",
    a: (
      <>
        We currently ship within the United States. Shipping options and rates
        are shown at checkout before you pay.
      </>
    ),
  },
  {
    q: "Do you offer returns for a change of mind?",
    a: (
      <>
        Because every item is made to order, we can't accept returns or
        exchanges for change of mind or the wrong size. Please review the{" "}
        <Link to="/size-guide">Size Guide</Link> and product details before
        ordering. Damaged or defective items are always covered — see{" "}
        <Link to="/returns">Returns &amp; Cancellations</Link>.
      </>
    ),
  },
];

const FaqPage: React.FC = () => {
  useDocumentMeta({
    title: "FAQ & Help",
    description:
      "Answers to common questions about The Americana Company — shipping times, sizing, cancellations, returns, tracking, and payments.",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <PageContainer hasPadding gapSize={GapSize.Small}>
      <PageSection className="flex justify-center py-4">
        <div className="max-w-3xl w-full flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h1 className="text-3xl playfair-text text-brown-dark">
              Frequently Asked Questions
            </h1>
            <p className="text-sm text-gray-600 lora-text">
              Can't find what you need?{" "}
              <Link to="/contact" className="text-brown underline">
                Contact us
              </Link>{" "}
              and we'll help.
            </p>
          </div>

          <div className="flex flex-col divide-y divide-accent">
            {FAQS.map((faq) => (
              <details key={faq.q} className="py-3 group">
                <summary className="cursor-pointer list-none flex justify-between items-center gap-3 text-lg text-brown-dark font-semibold">
                  <span>{faq.q}</span>
                  <span className="text-brown text-xl group-open:rotate-45 transition-transform">
                    +
                  </span>
                </summary>
                <div className="mt-2 text-sm text-gray-700 lora-text leading-relaxed">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </PageSection>
    </PageContainer>
  );
};

export default FaqPage;
